/**
 * Public Vacation Rentals Search Page
 * Estate Flow - Sebastian Alliance Group LLC
 * 
 * Public-facing page for guests to browse and search vacation rentals
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { 
  Search, 
  MapPin, 
  Calendar, 
  Users, 
  Star, 
  Heart,
  Filter,
  Grid,
  List,
  Wifi,
  Car,
  Waves,
  Utensils,
  Tv,
  Wind,
  Home,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const amenityIcons = {
  'wifi': Wifi,
  'parking': Car,
  'pool': Waves,
  'kitchen': Utensils,
  'tv': Tv,
  'ac': Wind,
};

const VacationRentalsSearch = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  
  // Search filters
  const [filters, setFilters] = useState({
    location: searchParams.get('location') || '',
    checkIn: searchParams.get('check_in') || '',
    checkOut: searchParams.get('check_out') || '',
    guests: searchParams.get('guests') || '',
    minPrice: 0,
    maxPrice: 1000,
    bedrooms: '',
    propertyType: '',
    amenities: []
  });

  useEffect(() => {
    searchProperties();
  }, [searchParams]);

  const searchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.location) params.append('location', filters.location);
      if (filters.checkIn) params.append('check_in', filters.checkIn);
      if (filters.checkOut) params.append('check_out', filters.checkOut);
      if (filters.guests) params.append('guests', filters.guests);
      if (filters.minPrice > 0) params.append('min_price', filters.minPrice);
      if (filters.maxPrice < 1000) params.append('max_price', filters.maxPrice);
      if (filters.bedrooms) params.append('bedrooms', filters.bedrooms);
      if (filters.propertyType) params.append('property_type', filters.propertyType);
      if (filters.amenities.length > 0) params.append('amenities', filters.amenities.join(','));
      params.append('page', pagination.page);
      
      const response = await axios.get(`${API}/public/vacation-rentals/properties?${params.toString()}`);
      const props = response.data.properties || [];
      // If no public properties, show sample properties for demo
      setProperties(props.length > 0 ? props : getSampleProperties());
      setPagination({
        page: response.data.page,
        total: props.length > 0 ? response.data.total : 4,
        totalPages: response.data.total_pages || 1
      });
    } catch (error) {
      console.error('Error searching properties:', error);
      // Show sample properties for demo
      setProperties(getSampleProperties());
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (filters.location) params.set('location', filters.location);
    if (filters.checkIn) params.set('check_in', filters.checkIn);
    if (filters.checkOut) params.set('check_out', filters.checkOut);
    if (filters.guests) params.set('guests', filters.guests);
    setSearchParams(params);
    searchProperties();
  };

  const toggleFavorite = (propertyId) => {
    if (favorites.includes(propertyId)) {
      setFavorites(favorites.filter(id => id !== propertyId));
      toast.success('Removed from favorites');
    } else {
      setFavorites([...favorites, propertyId]);
      toast.success('Added to favorites');
    }
  };

  const getSampleProperties = () => [
    {
      id: 'sample-1',
      name: 'Oceanfront Paradise Villa',
      property_type: 'villa',
      city: 'Miami Beach',
      country: 'USA',
      bedrooms: 4,
      bathrooms: 3,
      max_guests: 8,
      base_nightly_rate: 450,
      cleaning_fee: 150,
      rating: 4.9,
      review_count: 127,
      cover_photo: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800',
      amenities: ['wifi', 'pool', 'parking', 'kitchen', 'ac'],
      direct_booking_discount: 10
    },
    {
      id: 'sample-2',
      name: 'Downtown Luxury Condo',
      property_type: 'condo',
      city: 'Nassau',
      country: 'Bahamas',
      bedrooms: 2,
      bathrooms: 2,
      max_guests: 4,
      base_nightly_rate: 275,
      cleaning_fee: 100,
      rating: 4.8,
      review_count: 89,
      cover_photo: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800',
      amenities: ['wifi', 'parking', 'kitchen', 'tv', 'ac'],
      direct_booking_discount: 10
    },
    {
      id: 'sample-3',
      name: 'Beachfront Cottage',
      property_type: 'cottage',
      city: 'Montego Bay',
      country: 'Jamaica',
      bedrooms: 3,
      bathrooms: 2,
      max_guests: 6,
      base_nightly_rate: 325,
      cleaning_fee: 125,
      rating: 4.7,
      review_count: 64,
      cover_photo: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=800',
      amenities: ['wifi', 'pool', 'kitchen', 'ac'],
      direct_booking_discount: 10
    },
    {
      id: 'sample-4',
      name: 'Burj View Penthouse',
      property_type: 'penthouse',
      city: 'Dubai',
      country: 'UAE',
      bedrooms: 3,
      bathrooms: 3.5,
      max_guests: 6,
      base_nightly_rate: 850,
      cleaning_fee: 200,
      rating: 5.0,
      review_count: 42,
      cover_photo: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800',
      amenities: ['wifi', 'pool', 'parking', 'kitchen', 'tv', 'ac'],
      direct_booking_discount: 10
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <Home className="h-8 w-8 text-emerald-600" />
              <span className="text-xl font-bold text-gray-900">Estate Flow</span>
            </div>
            
            <div className="flex items-center gap-3">
              {/* For Property Owners */}
              <Button 
                variant="outline" 
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                onClick={() => navigate('/signup?role=vacation_rental')}
                data-testid="list-property-btn"
              >
                List Your Property
              </Button>
              <Button 
                variant="ghost"
                onClick={() => navigate('/vacation-rental/login')}
                data-testid="owner-login-btn"
              >
                Owner Login
              </Button>
              
              {/* Separator */}
              <div className="h-6 w-px bg-gray-300" />
              
              {/* For Guests */}
              <Button 
                variant="ghost" 
                onClick={() => navigate('/guest/login')}
                data-testid="guest-signin-btn"
              >
                Guest Sign In
              </Button>
              <Button 
                onClick={() => navigate('/guest/register')}
                data-testid="guest-signup-btn"
              >
                Book Now
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Property Owner CTA Banner */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-3">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-4">
          <span className="text-white font-medium">
            🏠 Own vacation rental properties? List them here and keep 96% of your revenue!
          </span>
          <Button 
            size="sm" 
            className="bg-white text-orange-600 hover:bg-gray-100"
            onClick={() => navigate('/signup?role=vacation_rental')}
          >
            Get Started - $99 Setup
          </Button>
        </div>
      </div>

      {/* Hero Search Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-white text-center mb-2">
            Find Your Perfect Vacation Rental
          </h1>
          <p className="text-emerald-100 text-center mb-8">
            Book direct & save up to 15% compared to OTAs
          </p>
          
          {/* Search Bar */}
          <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-2">
                <Label className="text-sm text-gray-600">Where</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search destinations..."
                    value={filters.location}
                    onChange={(e) => setFilters({...filters, location: e.target.value})}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm text-gray-600">Check In</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="date"
                    value={filters.checkIn}
                    onChange={(e) => setFilters({...filters, checkIn: e.target.value})}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm text-gray-600">Check Out</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="date"
                    value={filters.checkOut}
                    onChange={(e) => setFilters({...filters, checkOut: e.target.value})}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm text-gray-600">Guests</Label>
                <div className="relative">
                  <Users className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Select
                    value={filters.guests}
                    onValueChange={(value) => setFilters({...filters, guests: value})}
                  >
                    <SelectTrigger className="pl-10">
                      <SelectValue placeholder="Guests" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8,10,12].map(num => (
                        <SelectItem key={num} value={String(num)}>{num} {num === 1 ? 'Guest' : 'Guests'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between items-center mt-4">
              <Button 
                variant="ghost" 
                onClick={() => setShowFilters(!showFilters)}
                className="text-gray-600"
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? 'Hide Filters' : 'More Filters'}
              </Button>
              
              <Button onClick={handleSearch} className="bg-emerald-600 hover:bg-emerald-700">
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
            
            {/* Extended Filters */}
            {showFilters && (
              <div className="mt-6 pt-6 border-t grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Price Range (per night)</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm">${filters.minPrice}</span>
                    <Slider
                      value={[filters.minPrice, filters.maxPrice]}
                      min={0}
                      max={1000}
                      step={50}
                      onValueChange={([min, max]) => setFilters({...filters, minPrice: min, maxPrice: max})}
                      className="flex-1"
                    />
                    <span className="text-sm">${filters.maxPrice}+</span>
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-600">Bedrooms</Label>
                  <Select
                    value={filters.bedrooms || "any"}
                    onValueChange={(value) => setFilters({...filters, bedrooms: value === "any" ? "" : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      {[1,2,3,4,5,6].map(num => (
                        <SelectItem key={num} value={String(num)}>{num}+ bedrooms</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-600">Property Type</Label>
                  <Select
                    value={filters.propertyType || "any"}
                    onValueChange={(value) => setFilters({...filters, propertyType: value === "any" ? "" : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="villa">Villa</SelectItem>
                      <SelectItem value="condo">Condo</SelectItem>
                      <SelectItem value="apartment">Apartment</SelectItem>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="cottage">Cottage</SelectItem>
                      <SelectItem value="penthouse">Penthouse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-600">Amenities</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {['wifi', 'pool', 'parking', 'kitchen', 'ac'].map(amenity => (
                      <Badge
                        key={amenity}
                        variant={filters.amenities.includes(amenity) ? 'default' : 'outline'}
                        className="cursor-pointer capitalize"
                        onClick={() => {
                          if (filters.amenities.includes(amenity)) {
                            setFilters({...filters, amenities: filters.amenities.filter(a => a !== amenity)});
                          } else {
                            setFilters({...filters, amenities: [...filters.amenities, amenity]});
                          }
                        }}
                      >
                        {amenity}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Results Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {pagination.total > 0 ? `${pagination.total} properties found` : 'Featured Properties'}
            </h2>
            {filters.location && (
              <p className="text-gray-500">in {filters.location}</p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Property Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1,2,3,4,5,6,7,8].map(i => (
              <Card key={i} className="animate-pulse">
                <div className="h-48 bg-gray-200 rounded-t-lg" />
                <CardContent className="p-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
            : 'space-y-4'
          }>
            {properties.map(property => (
              <Card 
                key={property.id}
                className={`overflow-hidden hover:shadow-lg transition-shadow cursor-pointer ${
                  viewMode === 'list' ? 'flex' : ''
                }`}
                onClick={() => navigate(`/vacation-rentals/property/${property.id}`)}
              >
                <div className={`relative ${viewMode === 'list' ? 'w-72 flex-shrink-0' : ''}`}>
                  <img
                    src={property.cover_photo || 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800'}
                    alt={property.name}
                    className={`w-full object-cover ${viewMode === 'list' ? 'h-full' : 'h-48'}`}
                  />
                  {/* DEMO Ribbon */}
                  <div className="absolute top-0 left-0 overflow-hidden w-24 h-24 pointer-events-none">
                    <div className="absolute transform -rotate-45 bg-amber-500 text-white text-xs font-bold py-1 px-8 -left-6 top-4 shadow-md">
                      DEMO
                    </div>
                  </div>
                  <button
                    className="absolute top-3 right-3 p-2 bg-white/80 rounded-full hover:bg-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(property.id);
                    }}
                  >
                    <Heart 
                      className={`h-5 w-5 ${favorites.includes(property.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} 
                    />
                  </button>
                  {property.direct_booking_discount > 0 && (
                    <Badge className="absolute top-3 left-3 bg-emerald-600 ml-12">
                      Save {property.direct_booking_discount}%
                    </Badge>
                  )}
                </div>
                
                <CardContent className="p-4 flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900 line-clamp-1">{property.name}</h3>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {property.city}, {property.country}
                      </p>
                    </div>
                    {property.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-medium">{property.rating}</span>
                        <span className="text-gray-400 text-sm">({property.review_count})</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                    <span>{property.bedrooms} bed{property.bedrooms !== 1 ? 's' : ''}</span>
                    <span>•</span>
                    <span>{property.bathrooms} bath{property.bathrooms !== 1 ? 's' : ''}</span>
                    <span>•</span>
                    <span>{property.max_guests} guests</span>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mb-3">
                    {property.amenities?.slice(0, 4).map(amenity => {
                      const Icon = amenityIcons[amenity] || Wifi;
                      return (
                        <span key={amenity} className="text-gray-400">
                          <Icon className="h-4 w-4" />
                        </span>
                      );
                    })}
                  </div>
                  
                  <div className="flex items-end justify-between pt-2 border-t">
                    <div>
                      <span className="text-xl font-bold text-gray-900">${property.base_nightly_rate}</span>
                      <span className="text-gray-500 text-sm"> / night</span>
                    </div>
                    {property.calculated_total && (
                      <span className="text-sm text-gray-500">
                        ${property.calculated_total.total} total
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <Button
              variant="outline"
              disabled={pagination.page === 1}
              onClick={() => setPagination({...pagination, page: pagination.page - 1})}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>
            <span className="text-gray-600">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              disabled={pagination.page === pagination.totalPages}
              onClick={() => setPagination({...pagination, page: pagination.page + 1})}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!loading && properties.length === 0 && (
          <div className="text-center py-12">
            <Home className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No properties found</h3>
            <p className="text-gray-500 mb-4">Try adjusting your search filters</p>
            <Button onClick={() => setFilters({...filters, location: '', checkIn: '', checkOut: '', guests: ''})}>
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-bold text-lg mb-4">Estate Flow</h3>
              <p className="text-gray-400 text-sm">
                Book direct and save. We charge only 2% vs 15-20% OTA fees.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Guests</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="/vacation-rentals/search" className="hover:text-white">Search Properties</a></li>
                <li><a href="/guest/login" className="hover:text-white">Sign In</a></li>
                <li><a href="/guest/register" className="hover:text-white">Create Account</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Property Owners</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="/signup?role=vacation_rental" className="hover:text-white">List Your Property</a></li>
                <li><a href="/vacation-rental/login" className="hover:text-white">Owner Login</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="/faq" className="hover:text-white">FAQ</a></li>
                <li><a href="/contact" className="hover:text-white">Contact Us</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            © 2025 Estate Flow by Sebastian Alliance Group LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VacationRentalsSearch;
